import { computed } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { RESULT_COMMANDS } from "#ce/components/SpFinder/engine/result-commands";
import { useFinder } from "#ce/composables/useFinder";
import { useHostElement } from "#ce/composables/useHostElement";
import { resolveFinder } from "#ce/shared/finder-association";

const MOBILE_QUERY = "(max-width: 750px)";

/** Reads the enclosing finder's results settings and completed payload, and runs its result commands. */
export function useFinderResults() {
  const host = useHostElement();
  const finder = useFinder();
  const settings = computed(() => finder.settings.value?.results);
  const payload = computed(() => finder.completed.value?.result);
  const records = computed(() => {
    const results = payload.value?.results ?? [];
    const maxResults = settings.value?.maxResults;
    return maxResults ? results.slice(0, maxResults) : results;
  });
  const loading = computed(() => finder.request.value?.status === "loading");
  const empty = computed(() => Boolean(payload.value) && payload.value.result_count === 0);

  function finderElement() {
    return finder.connected.value ? resolveFinder(host).element : undefined;
  }

  function command(name) {
    return function runFinderResultCommand(...args) {
      return finderElement()?.[name]?.(...args);
    };
  }

  const commands = Object.fromEntries(RESULT_COMMANDS.map((name) => [name, command(name)]));

  // Runs a request the finder's kind declares, by its name.
  function request(name, input) {
    return finderElement()?.[name]?.(input);
  }

  return {
    host,
    kind: finder.kind,
    settings,
    payload,
    records,
    loading,
    empty,
    view: computed(() => finder.completed.value?.view),
    mobile: useMediaQuery(MOBILE_QUERY),
    commands,
    request,
    startDate: computed(() => finder.completed.value?.params?.slots_ui_start_date),
    requestKey: computed(() => finder.completed.value?.url),
  };
}
